import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { ACTION } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';

import type { AIPlugin } from '../../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import type { StepName } from '../../utils/analytics/analyticsFlowTypes';
import { useAnalyticsFlow } from '../../utils/analytics/analyticsFlowUtils';
import { rangeCharWordCount, useFireAIAnalyticsEvent } from '../../utils/analytics/utils';
import { getStepName } from '../../utils/error-handling';
import { FAILURE_REASON } from '../../utils/errors';
import { getSelectionCategory, getSelectionNodeTypes } from '../../utils/selection';

/**
 * Ref type for bridging the React-based AnalyticsFlow to the pure-pm plugin.
 * The ref is held in the plugin factory closure (aiPlugin) and set by
 * AnalyticsBridge via api.aiExperience.actions.setAnalyticsBridgeRef.
 * The pure-pm plugin receives a getter and calls it when firing steps.
 */
export type AnalyticsFlowBridgeRef = {
	current: {
		fireAnalyticsStep: (step: StepName, targetStep?: StepName) => void;
	} | null;
};

/**
 * Bridges analytics data from ProseMirror plugin state into the userFlow
 * analytics flow.
 *
 * On the pure-pm path, streaming operations happen in the ProseMirror plugin
 * without React involvement. This component sits between
 * AnalyticsFlowContextProvider and RenderScreen, giving it a stable lifecycle
 * that spans all screen transitions. This allows it to:
 *
 * - Accumulate state across screen changes (e.g., durations from loading
 *   through to accept/reject)
 * - Observe ProseMirror state changes continuously
 * - Sync data from the plugin world into the analytics flow
 */
export function AnalyticsBridge({
	api,
	children,
	uiState,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	uiState: string;
}) {
	const analyticsFlow = useAnalyticsFlow();
	const lastQueuedStep = useRef<StepName | undefined>();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const intl = useIntl();
	// Track whether the viewed event has already been fired for this session
	// to prevent double-firing on re-renders.
	const hasViewedFired = useRef(false);

	const { editorView, canInlineStreamChanges } = useContentComponentContext();

	// Read decoration state synchronously so that common attributes are
	// available before the first analytics step fires. The hook-based values
	// serve as a reactive fallback for later updates.
	const decorationState = editorView?.state
		? aiExperienceDecorationPluginKey.getState(editorView.state)
		: undefined;

	const {
		traceId,
		promptVersion,
		configItem: hookConfigItem,
		initialPositions: hookInitialPositions,
		sessionStatus,
		errorInfo,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		traceId: state.aiExperienceState?.aiDecorationExperiencePluginState.traceId,
		promptVersion: state.aiExperienceState?.aiDecorationExperiencePluginState.promptVersion,
		configItem: state.aiExperienceState?.aiDecorationExperiencePluginState.configItem,
		initialPositions: state.aiExperienceState?.aiDecorationExperiencePluginState.initialPositions,
		sessionStatus: state.aiExperienceState?.aiSessionPluginState?.status,
		errorInfo: state.aiExperienceState?.aiSessionPluginState?.errorInfo,
	}));

	const configItem = decorationState?.configItem ?? hookConfigItem;
	const initialPositions = decorationState?.initialPositions ?? hookInitialPositions;

	const isStreaming = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) =>
		Boolean(state.aiExperienceState?.isStreaming),
	);

	const isIdle = useRef(true);

	// Guards against duplicate prompt analytics fires across re-renders.
	const lastFiredSessionStatus = useRef<string | undefined>();

	// Track time spent in loading vs streaming for duration measurements.
	// These persist across screen transitions since this component doesn't unmount.
	const loadingStartTime = useRef(performance.now());
	const streamingStartTime = useRef<number>();

	if (!streamingStartTime.current && isStreaming) {
		streamingStartTime.current = performance.now();
	}

	useEffect(() => {
		if (uiState === 'idle' && !isIdle.current) {
			loadingStartTime.current = performance.now();
			isIdle.current = true;
			hasViewedFired.current = false;
			lastFiredSessionStatus.current = undefined;
		} else {
			isIdle.current = false;
		}
	}, [uiState]);

	// Record prompt status; actual analytics fire in the pendingStep effect
	// (the analyticsFlow is recreated when aiSessionId changes, so we cannot
	// enqueue here directly).
	useEffect(() => {
		if (sessionStatus === 'prompt' && lastFiredSessionStatus.current !== 'prompt') {
			lastFiredSessionStatus.current = 'prompt';
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionStatus]);

	// Fire error analytics (apiError or failed) when entering error status.
	// The analyticsFlow may be recreated between sessions (with a fresh invoke
	// queued), so we flush any queued step first, then enqueue and fire the error.
	useEffect(() => {
		if (sessionStatus !== 'error' || !errorInfo || lastFiredSessionStatus.current === 'error') {
			return;
		}

		lastFiredSessionStatus.current = 'error';

		const stepName = getStepName(errorInfo);

		// Flush any previously queued step (e.g. invoke from a recreated flow)
		analyticsFlow.fireQueuedStep(stepName);

		analyticsFlow.enqueueStep({
			stepName,
			attributes: {
				errorType: errorInfo.failureReason ?? FAILURE_REASON.UNKNOWN,
				sawAupViolation: errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION,
				...errorInfo,
			},
		});
		analyticsFlow.fireQueuedStep();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionStatus, errorInfo]);

	useEffect(() => {
		const streamingStart = streamingStartTime.current;
		const loadingStart = loadingStartTime.current;

		return () => {
			if (!streamingStart) {
				return;
			}

			analyticsFlow.addAttributes({
				durations: {
					timeSpentWithoutPartialPreview: streamingStart - loadingStart,
					timeSpentWithPartialPreview: performance.now() - streamingStart,
				},
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync traceId and promptVersion into common attributes when it becomes available from the backend
	useEffect(() => {
		if (traceId || promptVersion) {
			analyticsFlow.updateCommonAttributes({
				traceIds: traceId ? [traceId] : undefined,
				promptVersion,
			});
		}
	}, [traceId, promptVersion, analyticsFlow]);

	// Populate common analytics attributes
	useEffect(() => {
		if (!configItem) {
			return;
		}

		const backendModel = configItem.getBackendModel?.();
		const autoAcceptAIChanges = canInlineStreamChanges();
		const isInvokedFromEmptyPage = isEmptyDocument(editorView.state.doc);
		const isSmartLinkToolbarExpEnabled = expValEqualsNoExposure(
			'platform_editor_rovobutton_smartlink_toolbar_exp',
			'isEnabled',
			true,
		);

		analyticsFlow.updateCommonAttributes({
			backendModel,
			...analyticsFlow.getUserFlowCommonAttributes(configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: true,
			isInvokedFromEmptyPage,
			...(isSmartLinkToolbarExpEnabled && {
				has3pSources: decorationState?.has3pSources,
				sourceProduct: decorationState?.sourceProduct,
			}),
		});

		if (initialPositions) {
			const selectionCategory = getSelectionCategory(editorView, initialPositions);
			const { charCount, wordCount } = rangeCharWordCount(
				editorView.state.doc,
				initialPositions[0],
				initialPositions[1],
			);

			analyticsFlow.updateCommonAttributes({
				selectionCharCount: charCount,
				selectionWordCount: wordCount,
				selectionCategory,
			});

			const selectionNodes = getSelectionNodeTypes(editorView.state, initialPositions);
			analyticsFlow.updateCommonAttributes({ selectionNodes });
		}
	}, [
		analyticsFlow,
		configItem,
		initialPositions,
		editorView,
		canInlineStreamChanges,
		decorationState?.has3pSources,
		decorationState?.sourceProduct,
	]);

	// Defer analytics firing to a useEffect so that synchronous calls from the
	// ProseMirror plugin don't trigger the event mid-render.
	// We store the pending step in a ref and bump a counter to trigger the effect,
	// avoiding chained state updates (setting state inside an effect triggered by that state).
	const pendingStepRef = useRef<{
		step: StepName;
		targetStep?: StepName;
	} | null>(null);
	const [pendingStepVersion, setPendingStepVersion] = useState(0);

	const fireAnalyticsStep = useCallback((step: StepName, targetStep?: StepName) => {
		if (lastQueuedStep.current !== step) {
			lastQueuedStep.current = step;
			pendingStepRef.current = { step, targetStep };
			setPendingStepVersion((v) => v + 1);
		}
	}, []);

	useEffect(() => {
		const pendingStep = pendingStepRef.current;
		if (!pendingStep) {
			return;
		}
		pendingStepRef.current = null;

		const { step, targetStep } = pendingStep;

		// Inject the prompt step into the invoke→loading chain when we came
		// through the prompt screen (replicates: invoke → prompt → loading).
		if (step === 'invoke' && lastFiredSessionStatus.current === 'prompt') {
			lastFiredSessionStatus.current = 'invoke-after-prompt';

			analyticsFlow.fireQueuedStep('prompt');
			analyticsFlow.enqueueStep({
				stepName: 'prompt',
				attributes: {
					experienceName: configItem?.key,
					featureId: configItem?.featureId,
					promptTitle: configItem?.promptLabel
						? intl.formatMessage(configItem.promptLabel)
						: undefined,
					promptType: 'generate',
					usingCommandPalette: true,
				},
			});

			analyticsFlow.fireQueuedStep(targetStep);

			return;
		}

		// invoke is already queued on reset
		if (step !== 'invoke') {
			analyticsFlow.enqueueStep({ stepName: step });
		}

		// Fire the unified ACTION.VIEWED event when the stream has completed.
		// In the legacy path, this was handled by useInEditorDirectStreaming on isFinalStream.
		// In the pure-PM path (xstate migration), the streaming happens in ProseMirror plugins
		// without React. handleFinalize fires 'complete' after the stream ends, so we fire
		// ACTION.VIEWED here when the bridge processes the 'complete' step.
		if (step === 'complete' && !hasViewedFired.current) {
			const unifiedEventData = analyticsFlow.getUnifiedEventData();
			const isSmartLinkToolbarExpEnabled = expValEqualsNoExposure(
				'platform_editor_rovobutton_smartlink_toolbar_exp',
				'isEnabled',
				true,
			);
			if (
				unifiedEventData.invokedFrom !== 'streamingApi' &&
				unifiedEventData.invokedFrom !== 'confluenceTitleGenOnPaste'
			) {
				hasViewedFired.current = true;
				fireAIAnalyticsEvent({
					payload: createUnifiedAnalyticsPayload({
						action: ACTION.VIEWED,
						interactionID: unifiedEventData.interactionID,
						experienceName: unifiedEventData.experienceName,
						isProactive: false,
						invokedFrom: unifiedEventData.invokedFrom,
						...(isSmartLinkToolbarExpEnabled && {
							has3pSources: unifiedEventData.has3pSources,
							sourceProduct: unifiedEventData.sourceProduct,
						}),
						attributes: unifiedEventData.attributes,
						traceIds: unifiedEventData.traceIds,
					}),
				});
			}
		}

		analyticsFlow.fireQueuedStep(targetStep);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingStepVersion, analyticsFlow, fireAIAnalyticsEvent, configItem, intl]);

	// Expose fireAnalyticsStep to the pure-pm plugin via the plugin API (ref so plugin always calls current callback)
	const bridgeRef = React.useRef<AnalyticsFlowBridgeRef['current']>(null);
	useEffect(() => {
		bridgeRef.current = { fireAnalyticsStep };
		api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(bridgeRef);
		return () => {
			api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(null);
			bridgeRef.current = null;
		};
	}, [api, fireAnalyticsStep]);

	return <>{children}</>;
}
